/**
 * 📦 Reine Hilfsfunktionen für Paketsuche, Verwechslungsschutz, Duplikaterkennung
 * und OCR-toleranten String-Vergleich (ausgelagert aus AddPackageWithAI.js).
 */
import { extractHausnummer, splitSurname, isAutoRegalLetter } from '../services/printerService';
import { vintedCodeAusTracking, glsInVintedCode } from '../services/vintedNames';

// 🎨 Paketfarbe aus der KI (immer deutsch gespeichert: "braun", "weiß", "blau-weiß" usw.)
// für die Anzeige in die gewählte App-Sprache übersetzen, ohne den gespeicherten Wert zu ändern.
const FARB_UEBERSETZUNG = {
  nl: {
    braun: 'bruin',
    'weiß': 'wit',
    weiss: 'wit',
    schwarz: 'zwart',
    grau: 'grijs',
    blau: 'blauw',
    gelb: 'geel',
    'grün': 'groen',
    gruen: 'groen',
    rot: 'rood',
    orange: 'oranje',
    rosa: 'roze',
    lila: 'paars',
    beige: 'beige',
    bunt: 'kleurrijk'
  },
  en: {
    braun: 'brown',
    'weiß': 'white',
    weiss: 'white',
    schwarz: 'black',
    grau: 'grey',
    blau: 'blue',
    gelb: 'yellow',
    'grün': 'green',
    gruen: 'green',
    rot: 'red',
    orange: 'orange',
    rosa: 'pink',
    lila: 'purple',
    beige: 'beige',
    bunt: 'colorful'
  }
};

export function translatePackageColor(farbe, lang) {
  if (!farbe) return '';
  const code = String(lang || '').toLowerCase().slice(0, 2);
  const map = FARB_UEBERSETZUNG[code];
  if (!map) return farbe;
  return String(farbe)
    .split(/(\s*[-/,+]\s*|\s+)/)
    .map(part => {
      const key = part.trim().toLowerCase();
      return map[key] || part;
    })
    .join('');
}

// Auth-Helper: JWT-basierte User-Erkennung (ersetzt auth.currentUser)
export const getAuthUser = () => {
  try {
    const token = localStorage.getItem('kiosk_jwt_access');
    if (!token) return null;
    const payload = JSON.parse(atob(token.split('.')[1]));
    if (payload.exp * 1000 < Date.now()) return null;
    return { uid: payload.sub, email: payload.email };
  } catch { return null; }
};

// ⚠️ Verwechslungs-Schutz beim Einlagern: gleiche Hausnummer im NACHBARFACH erkennen.
// Regalfach in Buchstaben-Block + Zahl zerlegen ("B5"→B/5, "867"→''/867, "F255"→F/255, "CO5"→C/5).
export function parseRegal(r) {
  let s = String(r || '').toUpperCase().replace(/\s+/g, '');
  if (/^[A-Z]O\d+$/.test(s)) s = `${s[0]}0${s.slice(2)}`; // Tippfehler Buchstabe O statt 0 ("CO5" → "C05")
  const m = s.match(/^([A-Z]*)(\d*)$/);
  if (!m) return { p: s, n: null };
  return { p: m[1] || '', n: m[2] ? parseInt(m[2], 10) : null };
}

/**
 * Prüft, ob zwei Regalplätze dasselbe Fach bezeichnen — ignoriert führende Nullen ("C2" === "C02")
 * und Buchstabe-O-Tippfehler ("CO5" === "C05").
 */
export function regalGleich(a, b) {
  const sA = String(a || '').toUpperCase().replace(/\s+/g, '');
  const sB = String(b || '').toUpperCase().replace(/\s+/g, '');
  if (!sA || !sB) return false;
  if (sA === sB) return true;
  const pA = parseRegal(sA);
  const pB = parseRegal(sB);
  return pA.p === pB.p && pA.n !== null && pA.n === pB.n;
}

const _regalStatsCache = new WeakMap();

function _getRegalPrefixStats(pakete) {
  if (!Array.isArray(pakete) || pakete.length === 0) {
    return { paddedPrefs: new Set(), twoDigitPrefs: new Set(), openExact: new Set() };
  }
  let cached = _regalStatsCache.get(pakete);
  if (cached) return cached;
  const counts = new Map();
  const openExact = new Set();
  for (const p of pakete) {
    if (!p || p.deleted || !p.regalplatz) continue;
    const rp = String(p.regalplatz).toUpperCase().replace(/\s+/g, '');
    if (!p.ausgegebenAm && !p.zurueckgeschickt) openExact.add(rp);
    const m = rp.match(/^([A-Z]{1,2})(\d+)$/);
    if (!m) continue;
    const pref = m[1];
    const numStr = m[2];
    let c = counts.get(pref);
    if (!c) { c = { padded: 0, unpadded: 0, twoDigit: 0, threeDigit: 0 }; counts.set(pref, c); }
    if (numStr.length === 2 && numStr[0] === '0' && numStr[1] !== '0') c.padded++;
    else if (numStr.length === 1 && numStr[0] !== '0') c.unpadded++;
    else if (numStr.length === 2) c.twoDigit++;
    else if (numStr.length >= 3) c.threeDigit++;
  }
  const paddedPrefs = new Set();
  const twoDigitPrefs = new Set();
  for (const [pref, c] of counts.entries()) {
    if (c.padded > c.unpadded) paddedPrefs.add(pref);
    if ((c.padded + c.unpadded + c.twoDigit) >= 2 && c.threeDigit === 0) twoDigitPrefs.add(pref);
  }
  cached = { paddedPrefs, twoDigitPrefs, openExact };
  _regalStatsCache.set(pakete, cached);
  return cached;
}

/**
 * Normalisiert eine Regaleingabe anhand des bestehenden Shop-Systems:
 * - "CO5" → "C05" (Buchstabe O zwischen Einzelbuchstabe und Ziffer)
 * - "A006" → "A06" (versehentliche Doppelnull, sofern das Regal erwiesenermaßen 2-stellige Nummern nutzt)
 * - "C2" → "C02", wenn der Shop für Präfix "C" überwiegend zweistellige Nummern mit führender Null ("C01"…"C09") nutzt.
 */
export function normalizeRegalplatz(raw, pakete = []) {
  let s = String(raw || '').toUpperCase().replace(/\s+/g, '').replace(/^(?:RG:?|;+)/i, '');
  if (!s) return '';
  if (/^[A-Z]O\d+$/.test(s)) s = `${s[0]}0${s.slice(2)}`;
  const stats = _getRegalPrefixStats(pakete);
  const mTriple = s.match(/^([A-Z]{1,2})00+([1-9]\d?)$/);
  if (mTriple && stats.twoDigitPrefs.has(mTriple[1])) {
    s = mTriple[2].length === 1 ? `${mTriple[1]}0${mTriple[2]}` : `${mTriple[1]}${mTriple[2]}`;
  }
  const mSingle = s.match(/^([A-Z]{1,2})([1-9])$/);
  if (mSingle) {
    const pref = mSingle[1];
    const num = mSingle[2];
    if (stats.paddedPrefs.has(pref) || stats.openExact.has(`${pref}0${num}`)) {
      return `${pref}0${num}`;
    }
  }
  return s;
}

/**
 * 🗄️ Berechnet für Shops mit nummeriertem Boxen-System (z. B. A01–A24, B01–B23, C01–C25 + PL1–PL4)
 * eine 1-Tap-Schnellwahl aus gelernten Regal-Präfixen sowie aktuell freien / wenig belegten Boxen.
 */
export function berechneRegalSchnellwahl(pakete = [], currentInput = '') {
  if (!Array.isArray(pakete) || pakete.length < 6) {
    return { hatBoxSystem: false, prefixes: [], sonderPlaetze: [], vorschlaege: [], aktiverPrefix: '' };
  }
  const byPref = new Map();
  for (const p of pakete) {
    if (!p || p.deleted || !p.regalplatz) continue;
    const raw = String(p.regalplatz).toUpperCase().replace(/\s+/g, '');
    const parsed = parseRegal(raw);
    if (!parsed.p || parsed.p.length > 2 || parsed.n === null || parsed.n < 1 || parsed.n > 60) continue;
    let info = byPref.get(parsed.p);
    if (!info) {
      info = { pref: parsed.p, total: 0, nums: new Set(), maxN: 0, padded: 0, unpadded: 0, openByN: new Map() };
      byPref.set(parsed.p, info);
    }
    info.total++;
    info.nums.add(parsed.n);
    if (parsed.n > info.maxN) info.maxN = parsed.n;
    if (parsed.n < 10) {
      if (/^[A-Z]{1,2}0[1-9]$/.test(raw)) info.padded++;
      else info.unpadded++;
    }
    if (!p.ausgegebenAm && !p.zurueckgeschickt) {
      info.openByN.set(parsed.n, (info.openByN.get(parsed.n) || 0) + 1);
    }
  }

  const boxUnits = [];
  const sonderPlaetze = [];
  for (const info of byPref.values()) {
    if (info.nums.size >= 4 && info.maxN >= 5 && info.maxN <= 60) {
      boxUnits.push(info);
    } else if (info.nums.size >= 1 && info.maxN <= 8 && info.total >= 3 && info.pref.length >= 2) {
      const maxS = Math.min(info.maxN, 6);
      for (let i = 1; i <= maxS; i++) {
        const code = info.padded > info.unpadded && i < 10 ? `${info.pref}0${i}` : `${info.pref}${i}`;
        sonderPlaetze.push({ code, count: info.openByN.get(i) || 0 });
      }
    }
  }

  if (!boxUnits.length) {
    return { hatBoxSystem: false, prefixes: [], sonderPlaetze: [], vorschlaege: [], aktiverPrefix: '' };
  }

  boxUnits.sort((a, b) => a.pref.localeCompare(b.pref));
  const fmt = (info, n) => (info.padded > info.unpadded && n < 10 ? `${info.pref}0${n}` : `${info.pref}${n}`);
  const cleanIn = String(currentInput || '').toUpperCase().replace(/\s+/g, '');
  const typedPref = cleanIn.match(/^([A-Z]{1,2})\d*$/)?.[1] || '';
  const activeUnit = boxUnits.find((u) => u.pref === typedPref) || null;

  const vorschlaege = [];
  if (activeUnit) {
    const empty = [];
    const light = [];
    for (let n = 1; n <= activeUnit.maxN; n++) {
      const cnt = activeUnit.openByN.get(n) || 0;
      const code = fmt(activeUnit, n);
      if (cnt === 0) empty.push({ code, count: 0 });
      else if (cnt === 1) light.push({ code, count: 1 });
    }
    vorschlaege.push(...empty.slice(0, 8), ...light.slice(0, Math.max(0, 8 - empty.length)));
  } else {
    // Über alle Regal-Blöcke verteilt je 2 freie Fächer vorschlagen
    const perUnit = Math.max(2, Math.ceil(6 / boxUnits.length));
    for (const u of boxUnits) {
      let added = 0;
      for (let n = 1; n <= u.maxN && added < perUnit; n++) {
        if ((u.openByN.get(n) || 0) === 0) {
          vorschlaege.push({ code: fmt(u, n), count: 0 });
          added++;
        }
      }
    }
    if (vorschlaege.length < 4) {
      for (const u of boxUnits) {
        for (let n = 1; n <= u.maxN && vorschlaege.length < 6; n++) {
          if ((u.openByN.get(n) || 0) === 1) {
            const code = fmt(u, n);
            if (!vorschlaege.some((v) => v.code === code)) vorschlaege.push({ code, count: 1 });
          }
        }
      }
    }
  }

  return {
    hatBoxSystem: true,
    prefixes: boxUnits.map((u) => u.pref),
    sonderPlaetze: sonderPlaetze.slice(0, 6),
    vorschlaege: vorschlaege.slice(0, 8),
    aktiverPrefix: activeUnit ? activeUnit.pref : '',
  };
}

/**
 * Normalisiert Straßennamen ohne Hausnummer für den Shop-Adressen-Abgleich
 * ("Hemmstr. 120", "Hemmstr.120", "Hemmstraße 128", "Hemmstrasse 120" → "hemmstr").
 */
export function normStrasseBasis(s) {
  return String(s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/ß/g, 'ss')
    .replace(/\s+\d.*$/g, '')
    .replace(/\d+\s*[a-z]?\s*$/g, '')
    .replace(/(?:strasse|str\.?)\b/g, 'str')
    .replace(/[^a-z0-9 ]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Ermittelt alle Shop-/Abholpunkt-Adressen ("basis|hn") im aktuellen Bestand
 * (>= 3 verschiedene Empfänger mit derselben Straße + Hausnummer).
 * Ergänzt bei Hausnummern mit Endziffer 0 (z. B. "120") auch die typischen OCR-Lesefehler
 * einer durchgestrichenen Null ("12Ø" → "126", "128", "129", "12o", "12q"), ohne andere
 * Hausnummern derselben Straße vom Verwechslungsschutz auszuschließen.
 */
export function findeShopStrassen(pakete = []) {
  const byStrHn = new Map();
  for (const p of (pakete || [])) {
    if (!p || p.deleted || p.ausgegebenAm || p.zurueckgeschickt) continue;
    const basis = normStrasseBasis(p.streetAndNumber);
    const hn = String(extractHausnummer(p.streetAndNumber || '') || '').toLowerCase();
    const nameKey = String(p.name || '').trim().toLowerCase();
    if (!basis || basis.length < 3 || !hn || !nameKey) continue;
    const key = `${basis}|${hn}`;
    let set = byStrHn.get(key);
    if (!set) { set = new Set(); byStrHn.set(key, set); }
    set.add(nameKey);
  }
  const shopAdressen = new Set();
  for (const [key, set] of byStrHn.entries()) {
    if (set.size >= 3) {
      shopAdressen.add(key);
      const [basis, hn] = key.split('|');
      if (hn && (/^\d{2,}0$/.test(hn) || (set.size >= 5 && /^\d+0$/.test(hn)))) {
        const stem = hn.slice(0, -1);
        for (const v of ['6', '8', '9', 'o', 'q']) {
          shopAdressen.add(`${basis}|${stem}${v}`);
        }
      }
    }
  }
  return shopAdressen;
}

/**
 * Liest die im Setup hinterlegten gesperrten Empfänger-Begriffe (kiosk_ignore_recipient)
 * und zerlegt sie in ein Array einzelner Ausdrücke.
 */
export function getIgnoreRecipientTerms(customIgnore) {
  let raw = customIgnore;
  if (raw === undefined) {
    try { raw = localStorage.getItem('kiosk_ignore_recipient') || ''; } catch { raw = ''; }
  }
  return String(raw || '')
    .split(/[,;\n]+/)
    .map((x) => x.trim())
    .filter((x) => x.length >= 2);
}

let _cachedIgnoreRaw = null;
let _cachedIgnoreRegexes = [];

function _getIgnoreRecipientRegexes(customIgnore) {
  let raw = customIgnore;
  if (raw === undefined) {
    try { raw = localStorage.getItem('kiosk_ignore_recipient') || ''; } catch { raw = ''; }
  }
  const str = String(raw || '');
  if (str === _cachedIgnoreRaw) return _cachedIgnoreRegexes;
  _cachedIgnoreRaw = str;
  const terms = str
    .split(/[,;\n]+/)
    .map((x) => x.trim())
    .filter((x) => x.length >= 2)
    .sort((a, b) => b.length - a.length);
  _cachedIgnoreRegexes = terms.map((term) => {
    const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return new RegExp(`(?:^|\\s)${escaped}s?(?=\\s|$|[.,:;-])`, 'gi');
  });
  return _cachedIgnoreRegexes;
}

/**
 * Bereinigt einen erkannten Empfängernamen:
 * - extrahiert bei "z.Hd. <Person>" / "z. Hd. <Person>" die Person dahinter
 * - entfernt Paketshop-Zusätze ("DPD PaketShop - Selbstabholer", "Selbstabholer")
 * - entfernt vom Konto im Setup hinterlegte eigene Shop-Namen ("Top Phone Solution" → "")
 *   oder trennt sie vom dahinterstehenden Kundennamen ab ("Top Phone Solution Kai Blankemeyer" → "Kai Blankemeyer").
 */
export function bereinigeEmpfaengerName(rawName, customIgnore) {
  let s = String(rawName || '').trim();
  if (!s) return '';
  const mZhd = s.match(/\bz\.?\s*hd(?:\.|\b)\s*:?\s*(.+)$/i);
  if (mZhd && mZhd[1].trim().length >= 2) {
    s = mZhd[1].trim();
  }
  s = s
    .replace(/^(?:klant\s*\/\s*client|klant|ontvanger|empf[äa]nger|recipient|destinataire)\s*:?\s+/i, '')
    .replace(/\b(?:dpd|gls|hermes)\s+paketshop(?:\s*-\s*selbstabholer)?\b/gi, ' ')
    .replace(/\bpaketshop\s*-\s*selbstabholer\b/gi, ' ')
    .replace(/\bselbstabholer\b/gi, ' ')
    .replace(/\s+\(\d{1,3}[a-z]?\)\s*$/i, ''); // z. B. „MEREL VAN DER SLUIS (15)" auf DHL-NL-Routensticker
  const regexes = _getIgnoreRecipientRegexes(customIgnore);
  for (const rx of regexes) {
    s = s.replace(rx, ' ');
  }
  return s.replace(/\s+/g, ' ').replace(/^[.,:;\-/]+|[.,:;\-/]+$/g, '').trim();
}

/**
 * Prüft, ob ein Name ausschließlich aus einem gesperrten Shop-Begriff / Paketshop-Boilerplate besteht.
 */
export function istIgnorierterEmpfaengerName(name, customIgnore) {
  const raw = String(name || '').trim();
  if (!raw) return false;
  const cleaned = bereinigeEmpfaengerName(raw, customIgnore);
  return cleaned.length < 2;
}

// 🏤 Filial-/Packstation-Adresse? Dann ist die „Hausnummer" die Filialnummer (z. B.
// „Postfiliale 553“) — die tragen alle dieselbe, eine Verwechslung darüber gibt es nicht.
export function istFilialAdresse(s) {
  return /postfiliale|packstation|paketshop|filiale|postnummer|postfach|parcelshop|pakketpunt|servicepunt|afhaalpunt/i.test(String(s || ''));
}

// „Daneben" = gleicher Buchstaben-Block UND derselbe ZEHNER-Block UND höchstens
// REGAL_NACHBAR_DISTANZ Fächer Abstand. Die Regale sind in Zehnerschritten angeordnet
// (A10–A19 = ein Regal, A20–A29 = das nächste) — ein Zehnerwechsel ist ein Regalwechsel.
// Innerhalb eines Regals ist quer über den ganzen Zehner aber auch keine Verwechslung mehr.
//   A19 ↔ A23 → nein (anderes Regal) · A20 ↔ A24 → ja · A20 ↔ A25 → nein (zu weit)
export const REGAL_NACHBAR_DISTANZ = 4;

export function regaleBenachbart(a, b) {
  const A = parseRegal(a), B = parseRegal(b);
  if (A.p !== B.p) return false;                 // anderer Block → keine Verwechslungsgefahr
  if (A.n === null || B.n === null) return true; // rein alphabetisch → gleicher Block zählt
  if (Math.floor(A.n / 10) !== Math.floor(B.n / 10)) return false; // anderes Regal
  return Math.abs(A.n - B.n) <= REGAL_NACHBAR_DISTANZ;
}

/**
 * Prüft, ob zwei Empfängernamen zur selben Person gehören (inkl. Initialen wie „J. Martina" ↔ „Jerry Martina").
 */
export function sindGleichePerson(nameA, nameB) {
  const norm = (s) => String(s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9 ]+/g, ' ').replace(/\s+/g, ' ').trim();
  const nA = norm(nameA);
  const nB = norm(nameB);
  if (!nA || !nB) return false;
  if (nA === nB) return true;
  const a = splitSurname(nameA);
  const b = splitSurname(nameB);
  if (a.surname && b.surname && a.surname.length >= 3 && norm(a.surname) === norm(b.surname)) {
    const vA = nA.split(' ')[0] || '';
    const vB = nB.split(' ')[0] || '';
    const vornamenKonflikt = vA.length > 1 && vB.length > 1 && vA !== vB
      && !NAME_PARTIKEL.has(vA) && !NAME_PARTIKEL.has(vB);
    if (!vornamenKonflikt && (!a.initial || !b.initial || a.initial === b.initial)) {
      return true;
    }
  }
  return false;
}

/**
 * Liegt im Nachbarfach schon ein Paket mit DERSELBEN Hausnummer, aber ANDEREM Namen?
 * Gleicher Name = gehört zusammen → kein Hinweis. Nur Pakete, die wirklich im Regal liegen.
 * Rein lokal (kein Serverzugriff).
 */
export function findeHausnummerKonflikt(pakete, { regalplatz, streetAndNumber, name, id }) {
  const ziel = String(regalplatz || '').trim();
  const hn = extractHausnummer(streetAndNumber || '');
  if (!ziel || !hn) return null;
  // 🏤 Filial-/Packstation-Pakete ausnehmen: dort ist die „Hausnummer" die Filialnummer
  // (z. B. „Postfiliale 553") — die haben ALLE gleich, das wäre reiner Fehlalarm.
  if (istFilialAdresse(streetAndNumber)) return null;

  const aktive = (pakete || []).filter((p) => p && p.id !== id && !p.deleted && !p.ausgegebenAm && !p.zurueckgeschickt && p.regalplatz);

  // Wenn dieselbe Person bereits ein Paket in genau diesem Zielfach liegen hat („dazulegen"),
  // ist das Zielfach ausdrücklich richtig → niemals vor diesem Fach warnen!
  if (name && aktive.some((p) => regalGleich(p.regalplatz, ziel) && sindGleichePerson(name, p.name))) {
    return null;
  }

  // Wenn >= 3 verschiedene Empfänger im Bestand dieselbe Straße + Hausnummer haben,
  // ist es eine Shop-/Abholpunkt- bzw. Mehrparteien-Adresse (z. B. „Frobenstraat 15" oder „Hemmstr. 120") → kein Konflikt.
  // Auch OCR-Lesefehler der durchgestrichenen Null auf derselben Shop-Adresse („Hemmstr. 12Ø" → „Hemmstr. 128") ignorieren!
  const shopAdressen = findeShopStrassen(aktive);
  const eigeneBasis = normStrasseBasis(streetAndNumber);
  const hnKey = String(hn).toLowerCase();
  if (eigeneBasis && shopAdressen.has(`${eigeneBasis}|${hnKey}`)) return null;

  for (const p of aktive) {
    if (istFilialAdresse(p.streetAndNumber)) continue;               // Filialnummer zählt nicht
    const pBasis = normStrasseBasis(p.streetAndNumber);
    const pHn = extractHausnummer(p.streetAndNumber || '');
    if (pBasis && pHn && shopAdressen.has(`${pBasis}|${String(pHn).toLowerCase()}`)) continue; // Shop-Adresse auf Gegenseite zählt nicht
    if (pHn !== hn) continue;
    if (sindGleichePerson(name, p.name)) continue;                   // gleicher Empfänger → ok
    if (!regaleBenachbart(ziel, p.regalplatz)) continue;
    return { hausnummer: hn, regalplatz: p.regalplatz, name: p.name || '—' };
  }
  return null;
}

/**
 * 📦 Sendungsnummer-Suche. Ein gescannter Vinted-Abholcode (Kunden-App) ist JSON:
 * {"tracking_code":"1788…","pickup_code":"281223"} → daraus die Sendungsnummer nehmen.
 * Auch Handscanner (Tastatur-Modus) tippen dieses JSON ins Suchfeld — wird hier erkannt.
 */
export function scanZuSuchtext(text) {
  const s = String(text || '').trim();
  if (s.startsWith('{')) {
    try {
      const j = JSON.parse(s);
      if (j && (j.tracking_code || j.trackingCode)) return String(j.tracking_code || j.trackingCode);
    } catch { /* kein JSON */ }
  }
  return s;
}

/**
 * 🔐 Deterministische 4-stellige Abhol-PIN (1000..9999) aus Paket-ID für Abhol-E-Mails & Schnellsuche.
 * Funktioniert offline & online ohne DB-Schema-Änderung.
 */
export function getPackagePickupPin(paketOrId) {
  if (!paketOrId) return '';
  if (typeof paketOrId === 'object') {
    if (paketOrId.pickupPin && /^\d{4}$/.test(String(paketOrId.pickupPin))) {
      return String(paketOrId.pickupPin);
    }
    if (!paketOrId.id) return '';
  }
  const raw = typeof paketOrId === 'object' ? String(paketOrId.id || '') : String(paketOrId);
  if (!raw) return '';
  let hash = 2166136261;
  for (let i = 0; i < raw.length; i++) {
    hash ^= raw.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  const pin = 1000 + (Math.abs(hash >>> 0) % 9000);
  return String(pin);
}

export function trackingTreffer(paket, query) {
  if (!paket) return false;
  // 🔐 4-stellige Abhol-PIN aus Benachrichtigungs-E-Mail (z. B. "4829" oder "PIN 4829")
  const rawQuery = String(query || '').trim();
  const pinMatch = rawQuery.match(/^(?:PIN[:\s#-]*)?(\d{4})$/i);
  if (pinMatch) {
    const pkgPin = getPackagePickupPin(paket);
    if (pkgPin && pkgPin === pinMatch[1]) return true;
  }
  if (!paket.trackingNumber) return false;
  const q = scanZuSuchtext(query).toUpperCase().replace(/[^A-Z0-9]/g, '');
  if (q.length < 4 || !/\d/.test(q)) return false; // nur Suchen mit Ziffern
  const trk = String(paket.trackingNumber).toUpperCase().replace(/[^A-Z0-9]/g, '');
  // Beide Richtungen: getippte Teilnummer steckt in der Sendungsnr. ODER der gescannte Barcode
  // enthält die Sendungsnr. (DPD: Barcode „%03045RD05488807214686345528" ⊃ „05488807214686")
  if (trk.includes(q) || (trk.length >= 8 && q.includes(trk))) return true;
  // 📦 Vinted: entscheidend sind die letzten 6 Ziffern (Kurznummer). Die gescannte volle ID
  // „VGS0000910228923" findet so auch ein Paket, das vom Vinted-Bildschirm erfasst wurde („VGS228923").
  // Auch ein GLS-Partnerpaket innerhalb von Vinted (11–12 Ziffern): gescannter GLS-Code trifft das Paket, das
  // aus dem Vinted-Abgleich/Screenshot nur als „VGS"+Kurznummer angelegt wurde — und umgekehrt (13.10.2026).
  const kurz = vintedCodeAusTracking(trk) || glsInVintedCode(trk, paket.paketdienst);
  const qKurz = vintedCodeAusTracking(q) || (/^\d{11,12}$/.test(q) ? q.slice(-6) : '');
  return !!kurz && kurz === qKurz;
}

// 📷 Scanner in der Suche: Strichcodes UND 2D-Codes (der Regal-QR-Scanner bleibt bei „nur QR")
export const SUCH_SCAN_FORMATE = ['code_128', 'code_39', 'code_93', 'itf', 'ean_13', 'ean_8', 'codabar', 'data_matrix', 'qr_code', 'aztec', 'pdf417'];

export const NAME_PARTIKEL = new Set(['el', 'al', 'de', 'der', 'den', 'van', 'von', 'dos', 'da', 'di', 'la', 'le', 'ter', 'ten', 'het']);

// ⚠️ Gleiche Sendungsnummer schon im Bestand? (nur Hinweis „bitte prüfen", blockiert nie)
// Exakt gleiche Nummer — bei Vinted reicht die Kurznummer (Bildschirm-Erfassung speichert nur „VGS"+6 Ziffern).
export function findeGleicheSendung(pakete, { trackingNumber, id, paketdienst }) {
  const t = String(trackingNumber || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  if (t.length < 8) return null;
  // Kurznummer auch für ein GLS-Partnerpaket innerhalb von Vinted (11–12 Ziffern, Paketdienst Vinted)
  const tKurz = vintedCodeAusTracking(t) || glsInVintedCode(t, paketdienst);
  for (const p of (pakete || [])) {
    if (!p || p.id === id || p.deleted || p.ausgegebenAm || p.zurueckgeschickt) continue;
    const u = String(p.trackingNumber || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (!u) continue;
    if (u === t || (tKurz && tKurz === (vintedCodeAusTracking(u) || glsInVintedCode(u, p.paketdienst)))) {
      return { regalplatz: p.regalplatz || '', name: p.name || '' };
    }
  }
  return null;
}

/**
 * 📦 Hat dieser Empfänger schon Pakete im Regal? Dann sollen neue dazugelegt werden
 * (Kunden mit 10+ Paketen). Treffer: gleicher Name (normalisiert) ODER gleicher
 * Sortier-Nachname + gleiche Vornamens-Initiale, sofern die Straßen nicht widersprechen.
 * Liefert das häufigste Fach dieses Empfängers: {regalplatz, anzahl, name, sicher} | null.
 */
export function findeGleichenEmpfaenger(pakete, { name, streetAndNumber, id }) {
  const cleanedName = bereinigeEmpfaengerName(name);
  if (cleanedName.length < 2) return null;
  // Platzhalter („Vinted 123-456", „#001") nie als gleichen Empfänger gruppieren
  if (/^(?:vinted\s+\d|#\d+$|\d+$)/i.test(cleanedName)) return null;
  const norm = (s) => String(s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9 ]+/g, ' ').replace(/\s+/g, ' ').trim();
  const n = norm(cleanedName);
  if (n.length < 3) return null;
  const a = splitSurname(cleanedName);
  const strasse = norm(streetAndNumber);
  const faecher = new Map();
  let anzeigeName = '';
  // „sicher" nur bei exakt gleichem Namen MIT ausgeschriebenem Vornamen. „C. Vogel" kann
  // Celine ODER Cross Vogel sein (echter Fall, Green Goat) → dann nur Hinweis, kein Vorschlag.
  const vornameAus = (x) => (x.split(' ')[0] || '').length > 1 && x.split(' ').length > 1;
  let sicher = false;
  const exakt = new Map(); // Fächer NUR der exakt gleichen (vollen) Namen
  for (const p of (pakete || [])) {
    if (!p || p.id === id) continue;
    if (p.deleted || p.ausgegebenAm || p.zurueckgeschickt) continue; // nur aktueller Bestand
    if (!p.regalplatz) continue;
    const pNameClean = bereinigeEmpfaengerName(p.name);
    if (!pNameClean) continue;
    const pNorm = norm(pNameClean);
    let passt = pNorm === n;
    if (!passt && a.surname && a.surname.length >= 3) {
      const b = splitSurname(pNameClean);
      const pStr = norm(p.streetAndNumber);
      // Ausgeschriebene, VERSCHIEDENE Vornamen = verschiedene Personen („Jan" ≠ „Jeroen");
      // nur „M." ↔ „Michelle" (Initiale) darf zusammenpassen.
      const vA = n.split(' ')[0] || '';
      const vB = pNorm.split(' ')[0] || '';
      const vornamenKonflikt = vA.length > 1 && vB.length > 1 && vA !== vB
        && !NAME_PARTIKEL.has(vA) && !NAME_PARTIKEL.has(vB);
      passt = b.surname && norm(b.surname) === norm(a.surname)
        && !vornamenKonflikt
        && (!a.initial || !b.initial || a.initial === b.initial)
        && (!strasse || !pStr || strasse === pStr);
    }
    if (!passt) continue;
    const fachKan = normalizeRegalplatz(p.regalplatz, pakete) || p.regalplatz;
    if (pNorm === n && vornameAus(n)) {
      sicher = true;
      exakt.set(fachKan, (exakt.get(fachKan) || 0) + 1);
    }
    faecher.set(fachKan, (faecher.get(fachKan) || 0) + 1);
    if (!anzeigeName || (pNorm === n)) anzeigeName = pNameClean || anzeigeName;
  }
  if (!faecher.size) return null;
  // Sicherer Treffer → Fach NUR aus den exakten Treffern (sonst landet Celine bei „C. Vogel")
  const quelle = sicher ? exakt : faecher;
  let best = null;
  let gesamt = 0;
  for (const [fach, c] of quelle) { gesamt += c; if (!best || c > best[1]) best = [fach, c]; }
  return { regalplatz: best[0], anzahl: gesamt, name: anzeigeName, sicher };
}

/**
 * 🎁 Findet alle weiteren offenen Pakete derselben Person im aktuellen Bestand
 * (für die 1-Tap-Sammelausgabe im Ausgabe-Overlay, wenn ein Kunde mehrere einzelne Pakete hat).
 * Sicherheit gegen Initialen-Verwechslung („J. Schmidt" ↔ „Jan Schmidt" vs. „Julia Schmidt"):
 * Sammelausgabe nur bei exakt gleichem vollem Namen (ausgeschriebener Vorname) ODER wenn
 * sowohl Name/Initiale als auch die Kunden-Heimadresse (Straße + Hausnummer, nicht Shop-Adresse) übereinstimmen.
 */
export function findeWeitereOffenePakete(pakete = [], ausgegebenesPaket = null) {
  if (!ausgegebenesPaket || !ausgegebenesPaket.name || ausgegebenesPaket.isDemo) return [];
  const cleanTarget = bereinigeEmpfaengerName(ausgegebenesPaket.name);
  if (cleanTarget.length < 3) return [];
  if (/^(?:vinted\s+\d|#\d+$|\d+$)/i.test(cleanTarget)) return [];
  const norm = (s) => String(s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9 ]+/g, ' ').replace(/\s+/g, ' ').trim();
  const targetNorm = norm(cleanTarget);
  const targetParts = targetNorm.split(' ').filter(Boolean);
  const targetHatVollenVornamen = targetParts.length >= 2 && targetParts[0].length > 1;
  const shopAdressen = findeShopStrassen(pakete);
  const targetBasis = normStrasseBasis(ausgegebenesPaket.streetAndNumber);
  const targetHn = String(extractHausnummer(ausgegebenesPaket.streetAndNumber || '') || '').toLowerCase();
  const targetIstHeimAdresse = Boolean(
    targetBasis && targetHn && !istFilialAdresse(ausgegebenesPaket.streetAndNumber) && !shopAdressen.has(`${targetBasis}|${targetHn}`)
  );

  return (pakete || []).filter((p) => {
    if (!p || p.id === ausgegebenesPaket.id || p.isDemo) return false;
    if (p.deleted || p.ausgegebenAm || p.zurueckgeschickt) return false;
    const cleanP = bereinigeEmpfaengerName(p.name);
    if (!cleanP || cleanP.length < 3 || /^(?:vinted\s+\d|#\d+$|\d+$)/i.test(cleanP)) return false;
    const pNorm = norm(cleanP);
    if (targetHatVollenVornamen && pNorm === targetNorm) {
      // Falls beide Pakete eine echte Heimadresse tragen, dürfen sie sich nicht widersprechen
      const pBasis = normStrasseBasis(p.streetAndNumber);
      const pHn = String(extractHausnummer(p.streetAndNumber || '') || '').toLowerCase();
      const pIstHeimAdresse = Boolean(pBasis && pHn && !istFilialAdresse(p.streetAndNumber) && !shopAdressen.has(`${pBasis}|${pHn}`));
      if (targetIstHeimAdresse && pIstHeimAdresse && (targetBasis !== pBasis || targetHn !== pHn)) return false;
      return true;
    }
    if (!targetIstHeimAdresse) return false;
    const pBasis = normStrasseBasis(p.streetAndNumber);
    const pHn = String(extractHausnummer(p.streetAndNumber || '') || '').toLowerCase();
    if (pBasis !== targetBasis || pHn !== targetHn) return false;
    return sindGleichePerson(cleanTarget, cleanP);
  });
}

/**
 * Normalisiert Strings für OCR-tolerante Suche (Umlaute + OCR-Fehler)
 * @param {string} str - Der zu normalisierende String
 * @returns {string} Normalisierter String
 */
export const normalizeForSearch = (str) => {
  if (!str) return '';
  return str
    .toLowerCase()
    .trim()
    // Umlaute normalisieren
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    // Whitespace vereinheitlichen
    .replace(/\s+/g, ' ');
};

/**
 * Numerische Normalisierung für PLZ/Hausnummern-Suche.
 * Gleicht OCR-typische Buchstaben-Zahl-Verwechslungen aus.
 */
export const normalizeForNumericSearch = (str) => {
  if (!str) return '';
  return str
    .toLowerCase()
    .replace(/[oO]/g, '0')
    .replace(/[lIi]/g, '1')
    .replace(/[sS]/g, '5')
    .replace(/[zZ]/g, '2')
    .replace(/[bB]/g, '8')
    .replace(/\//g, '7');
};

/**
 * OCR-Ersetzungstabelle für häufige Verwechslungen
 */
export const OCR_REPLACEMENTS = {
  '0': ['o', 'O'],
  'o': ['0', 'O'],
  'O': ['0', 'o'],
  '1': ['l', 'I', 'i'],
  'l': ['1', 'I', 'i'],
  'I': ['1', 'l', 'i'],
  'i': ['1', 'l', 'I'],
  '5': ['s', 'S'],
  's': ['5', 'S'],
  'S': ['5', 's'],
  '8': ['B', 'b'],
  'B': ['8', 'b'],
  'b': ['8', 'B'],
  '2': ['z', 'Z'],
  'z': ['2', 'Z'],
  'Z': ['2', 'z'],
  '6': ['G'],
  'G': ['6'],
  '/': ['7', '1'],
  '7': ['/'],
};

/**
 * Erstellt Varianten eines Strings mit OCR-Korrekturen
 * @param {string} str - Eingabestring
 * @returns {string[]} Array von Varianten
 */
export const createOCRVariants = (str) => {
  const variants = [str];
  for (let i = 0; i < str.length; i++) {
    const char = str[i];
    if (OCR_REPLACEMENTS[char]) {
      OCR_REPLACEMENTS[char].forEach(replacement => {
        const variant = str.substring(0, i) + replacement + str.substring(i + 1);
        variants.push(variant);
      });
    }
  }
  return variants;
};

/**
 * Berechnet Levenshtein-Distanz zwischen zwei Strings
 * @param {string} a - Erster String
 * @param {string} b - Zweiter String
 * @returns {number} Distanz
 */
export const levenshteinDistance = (a, b) => {
  const matrix = [];
  for (let i = 0; i <= b.length; i++) {
    matrix[i] = [i];
  }
  for (let j = 0; j <= a.length; j++) {
    matrix[0][j] = j;
  }
  for (let i = 1; i <= b.length; i++) {
    for (let j = 1; j <= a.length; j++) {
      if (b.charAt(i - 1) === a.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j] + 1
        );
      }
    }
  }
  return matrix[b.length][a.length];
};

/**
 * Prüft ob zwei Strings ähnlich sind (toleriert OCR-Fehler)
 * @param {string} str1 - Erster String
 * @param {string} str2 - Zweiter String (Suchbegriff)
 * @param {number} threshold - Ähnlichkeitsschwelle (0-1)
 * @returns {boolean} True wenn ähnlich
 */
export const isSimilar = (str1, str2, threshold = 0.8) => {
  if (!str1 || !str2) return false;
  
  // Normalisiere Strings
  const s1 = str1.toLowerCase().trim();
  const s2 = str2.toLowerCase().trim();
  
  // Exakte Übereinstimmung
  if (s1.includes(s2) || s2.includes(s1)) return true;
  
  // Normalisierte Übereinstimmung (Umlaute + OCR)
  const s1Norm = normalizeForSearch(str1);
  const s2Norm = normalizeForSearch(str2);
  if (s1Norm.includes(s2Norm) || s2Norm.includes(s1Norm)) return true;
  
  // Prüfe ob eine OCR-Variante matched
  const variants = createOCRVariants(s2);
  if (variants.some(v => s1.includes(v))) return true;
  
  // Levenshtein-Distanz für ähnliche Strings
  const distance = levenshteinDistance(s1, s2);
  const maxLen = Math.max(s1.length, s2.length);
  const similarity = 1 - distance / maxLen;
  
  return similarity >= threshold;
};

/**
 * Konvertiert deutsche Farbnamen zu CSS-Farben
 */
export const getCssColor = (farbe) => {
  if (!farbe) return null;
  const farbeLower = farbe.toLowerCase();
  const colorMap = {
    'weiß': '#FFFFFF', 'weiss': '#FFFFFF', 'white': '#FFFFFF',
    'schwarz': '#000000', 'black': '#000000',
    'rot': '#FF0000', 'red': '#FF0000',
    'blau': '#0000FF', 'blue': '#0000FF',
    'grün': '#00FF00', 'gruen': '#00FF00', 'green': '#00FF00',
    'gelb': '#FFFF00', 'yellow': '#FFFF00',
    'orange': '#FFA500',
    'lila': '#800080', 'purple': '#800080', 'violett': '#800080',
    'rosa': '#FFC0CB', 'pink': '#FFC0CB',
    'braun': '#8B4513', 'brown': '#8B4513',
    'grau': '#808080', 'grey': '#808080', 'gray': '#808080',
    'türkis': '#40E0D0', 'tuerkis': '#40E0D0', 'cyan': '#00FFFF',
    'beige': '#F5F5DC',
    'silber': '#C0C0C0', 'silver': '#C0C0C0',
    'gold': '#FFD700'
  };
  return colorMap[farbeLower] || farbeLower;
};

// 🛡️ Fremd-Überschreibungs-Schutz.
export function isForeignOverwrite(existing, incoming) {
  if (!existing || !incoming) return false;
  const toks = (s) => new Set(String(s || '').toLowerCase().split(/[^a-zà-ÿ0-9]+/).filter((w) => w.length >= 3));
  const eName = toks(existing.name);
  const iName = toks(incoming.name);
  if (!eName.size || !iName.size) return false;          // ohne Namen keine sichere Aussage
  const nameShare = [...iName].some((t) => eName.has(t));
  if (nameShare) return false;                            // gemeinsamer Namensteil → echte Korrektur
  const norm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  const eTrk = norm(existing.trackingNumber);
  const iTrk = norm(incoming.trackingNumber);
  const trackingChanged = (eTrk || iTrk) && eTrk !== iTrk;
  return trackingChanged;                                 // Name völlig anders UND Tracking anders → fremd
}

